import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Plus, ArrowRight, Flame, FileText, Euro, CheckCircle2, UserPlus, Clock, X, Loader2 } from "lucide-react";
import { formatEUR } from "@/lib/constants";
import ClientPurchaseWizard from "@/components/ClientPurchaseWizard";

export default function AgencyClients() {
  const { checkAuth } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [pending, setPending] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(searchParams.get("create") === "1");
  const [switching, setSwitching] = useState("");
  const [cancellingId, setCancellingId] = useState("");
  const [pollingPcid, setPollingPcid] = useState("");

  const refresh = async () => {
    try {
      setLoading(true);
      const [r, rp] = await Promise.all([
        api.get("/agency/clients"),
        api.get("/agency/client-checkout/pending").catch(() => ({ data: [] })),
      ]);
      setClients(r.data.clients || []);
      setActiveId(r.data.active_client_id || "");
      setPending(Array.isArray(rp.data) ? rp.data : []);
    } catch (e) {
      toast.error("Mandanten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, rp] = await Promise.all([
          api.get("/agency/clients"),
          api.get("/agency/client-checkout/pending").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setClients(r.data.clients || []);
        setActiveId(r.data.active_client_id || "");
        setPending(Array.isArray(rp.data) ? rp.data : []);
      } catch {
        if (!cancelled) toast.error("Mandanten konnten nicht geladen werden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // URL-Query: checkout=success|cancelled & pcid=... — deferred (vermeidet R19 set-state-in-effect)
  useEffect(() => {
    const status = searchParams.get("checkout");
    const pcid = searchParams.get("pcid");
    if (!status) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      if (cancelled) return;
      if (status === "success" && pcid) {
        toast.success("Zahlung erfolgreich – Mandant wird angelegt …");
        setPollingPcid(pcid);
      } else if (status === "cancelled" && pcid) {
        toast("Checkout abgebrochen", { description: "Du kannst das Paket jederzeit erneut kaufen." });
        api.post(`/agency/client-checkout/${pcid}/cancel`).catch(() => {});
      }
      const next = new URLSearchParams(searchParams);
      next.delete("checkout");
      next.delete("pcid");
      next.delete("create");
      setSearchParams(next, { replace: true });
    }, 0);
    return () => { cancelled = true; clearTimeout(handle); };
  }, []);

  // Polling für eingehende Webhook-Bestätigung
  useEffect(() => {
    if (!pollingPcid) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const r = await api.get(`/agency/client-checkout/${pollingPcid}/status`);
        if (r.data?.status === "completed" && r.data?.resulting_company_id) {
          clearInterval(interval);
          setPollingPcid("");
          toast.success(`Mandant "${r.data?.company?.name || r.data?.company_name}" wurde erstellt`);
          await refresh();
        } else if (r.data?.status === "cancelled" || r.data?.status === "expired") {
          clearInterval(interval);
          setPollingPcid("");
        }
      } catch {
        /* ignore */
      }
      if (attempts >= 30) {
        // ~ 30 * 2s = 60s; danach still beenden
        clearInterval(interval);
        setPollingPcid("");
        refresh();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [pollingPcid]);

  const onSwitch = async (company_id) => {
    if (company_id === activeId) return;
    setSwitching(company_id);
    try {
      const r = await api.post("/agency/switch-client", { company_id });
      toast.success(`Gewechselt zu ${r.data.active_client_name}`);
      setActiveId(company_id);
      await checkAuth();
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wechsel fehlgeschlagen");
    } finally {
      setSwitching("");
    }
  };

  const onCancelPending = async (pcid) => {
    setCancellingId(pcid);
    try {
      await api.post(`/agency/client-checkout/${pcid}/cancel`);
      toast.success("Pending-Checkout abgebrochen");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abbrechen fehlgeschlagen");
    } finally {
      setCancellingId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="agency-clients-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Mandanten
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Verwalten Sie alle Solar-Kunden Ihrer Agentur. Neue Mandanten werden über einen Checkout-First-Flow angelegt.
          </p>
        </div>
        <Button
          data-testid="create-client-btn"
          onClick={() => setWizardOpen(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Neuer Mandant
        </Button>
      </div>

      {/* Pending-Checkouts */}
      {pending.length > 0 && (
        <Card className="p-4 bg-amber-50/60 border-amber-200" data-testid="pending-checkouts-card">
          <p className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Offene Zahlungen ({pending.length})
          </p>
          <div className="space-y-2">
            {pending.map((p) => (
              <div
                key={p.pending_checkout_id}
                data-testid={`pending-row-${p.pending_checkout_id}`}
                className="flex items-center justify-between gap-3 flex-wrap bg-white border border-amber-200 rounded p-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{p.company_name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {p.plan_name?.replace(/^Client /, "")} · {p.billing_cycle === "yearly" ? "Jährlich" : "Monatlich"} · {p.email}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {pollingPcid === p.pending_checkout_id && (
                    <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Warte auf Webhook …
                    </span>
                  )}
                  <Button
                    data-testid={`cancel-pending-${p.pending_checkout_id}`}
                    size="sm"
                    variant="outline"
                    disabled={cancellingId === p.pending_checkout_id}
                    onClick={() => onCancelPending(p.pending_checkout_id)}
                    className="h-8 text-xs"
                  >
                    {cancellingId === p.pending_checkout_id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <><X className="w-3 h-3 mr-1" /> Abbrechen</>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="p-8 text-center text-slate-500">Wird geladen …</Card>
      ) : clients.length === 0 ? (
        <Card className="p-10 text-center" data-testid="clients-empty-state">
          <Building2 className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600 mb-4">Noch keine Mandanten angelegt.</p>
          <Button onClick={() => setWizardOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="empty-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Ersten Mandanten kaufen
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clients.map(c => {
            const isActive = c.company_id === activeId;
            return (
              <Card
                key={c.company_id}
                data-testid={`client-card-${c.company_id}`}
                className={`p-5 ${isActive ? "ring-2 ring-orange-500 bg-orange-50/40" : "hover:shadow-md"} transition-shadow`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{c.company_name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.contact_person || "–"} · {c.email || "–"}</p>
                  </div>
                  {isActive && (
                    <Badge className="bg-orange-600 hover:bg-orange-600 text-white shrink-0">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Aktiv
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-4">
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-slate-500 flex items-center gap-1"><FileText className="w-3 h-3"/>Leads/Mon</p>
                    <p className="font-bold text-slate-900">{c.leads_this_month}/{c.monthly_lead_limit}</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-slate-500 flex items-center gap-1"><Flame className="w-3 h-3"/>Hot</p>
                    <p className="font-bold text-slate-900">{c.hot_leads}</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-slate-500 flex items-center gap-1"><Euro className="w-3 h-3"/>Gerettet</p>
                    <p className="font-bold text-slate-900">{formatEUR(c.recovered_revenue || 0)}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-xs text-slate-500">{c.plan_name} · {c.open_offers} offene Angebote</span>
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid={`open-users-${c.company_id}`}
                      onClick={() => nav(`/agency/clients/${c.company_id}/users`)}
                      size="sm"
                      variant="ghost"
                      className="text-xs h-8"
                      title="Nutzer & Einladungen"
                    >
                      <UserPlus className="w-3 h-3 mr-1" /> Nutzer
                    </Button>
                    <Button
                      data-testid={`switch-client-${c.company_id}`}
                      disabled={isActive || switching === c.company_id}
                      onClick={() => onSwitch(c.company_id)}
                      size="sm"
                      variant={isActive ? "outline" : "default"}
                      className={isActive ? "h-8" : "bg-slate-900 hover:bg-slate-800 text-white h-8"}
                    >
                      {switching === c.company_id ? "..." : isActive ? "Aktiv" : (<>Öffnen <ArrowRight className="w-3 h-3 ml-1"/></>)}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ClientPurchaseWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onStarted={({ pcid }) => { setPollingPcid(pcid); }}
      />
    </div>
  );
}
